import { getParent, types, type Instance } from 'mobx-state-tree';
import AttributesInspector from './attributes_inspector';
import type { IInspectorParent } from './inspector_parent';

/** The change keys stored by the event inspector. */
type EventChangeKey = 'name' | 'description';

/**
 * Store the changes in the event inspector that are currently pending.
 */
const EventInspectorChanges = types
  .model('EventInspectorChanges', {
    name: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return self.name !== null || self.description !== null;
    },
  }))
  .actions((self) => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the attribute and clears
     * the store change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: EventChangeKey, original: string | null, change: string | null) {
      if (original !== change) {
        self[key] = change;
      } else {
        self[key] = null;
      }
    },
    discard() {
      self.name = null;
      self.description = null;
    },
  }));

/**
 * UI state for the event inspector
 */
const EventInspector = types
  .model('EventInspector', {
    // the pending changes
    changes: types.optional(EventInspectorChanges, () =>
      EventInspectorChanges.create({ name: null, description: null })
    ),
    // the state for the nested attributes inspector, see
    // `attributes_inspector.js`
    attributesInspector: types.optional(AttributesInspector, () => AttributesInspector.create({})),
  })
  .views((self) => ({
    // We are dirty when we have changes ourself or the nested attributes
    // inspector is dirty
    get dirty() {
      return self.changes.dirty || self.attributesInspector.dirty;
    },
  }))
  .actions((self) => ({
    // Discard our own changes and those of the nested attributes inspector
    discardChanges() {
      self.changes.discard();
      self.attributesInspector.discardChanges();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type IEventInspector = Instance<typeof EventInspector>;

export default EventInspector;
